import type { Graphics } from 'pixi.js';
import { smooth } from '../../burst';
import { lerpColor } from '../../color';
import { hsl, type Body, type Kit, type WorldDress, type WorldFx } from '../../world';

const TAU = Math.PI * 2;
const RIBS = 8;
const GOURD_RIBS = 6;
const CANDLES = 8;
const BATS = 6;
const MINIS = 8;
const LEAF = hsl(125, 55, 68);
const BAT: readonly number[] = [
  0, -0.2, 0.13, -0.44, 0.22, -0.16, 0.52, -0.42, 1, -0.16, 0.8, 0.04, 0.66, -0.03, 0.52, 0.13, 0.36, 0.02, 0.2, 0.18, 0, 0.3,
];

function ribs(k: Kit, body: Body, count: number, curl: number, color: number, alpha: number, width: number): void {
  const { g } = k;
  const r = body.radius;
  for (let i = 0; i < count; i++) {
    const a = (i / count) * TAU + 0.3;
    const at = (rr: number, turn: number): [number, number] => [
      body.x + Math.cos(a + turn) * r * rr * body.scaleX,
      body.y + Math.sin(a + turn) * r * rr * body.scaleY,
    ];
    const [x0, y0] = at(0.2, 0);
    const [cx, cy] = at(0.62, curl * 0.6);
    const [x1, y1] = at(0.94, curl);
    g.moveTo(x0, y0).quadraticCurveTo(cx, cy, x1, y1);
  }
  g.stroke({ width, color, alpha, cap: 'round' });
}

function leaf(g: Graphics, x: number, y: number, len: number, angle: number): void {
  const ux = Math.cos(angle);
  const uy = Math.sin(angle);
  const tx = x + ux * len;
  const ty = y + uy * len;
  const mx = x + ux * len * 0.5;
  const my = y + uy * len * 0.5;
  const w = len * 0.42;
  g.moveTo(x, y)
    .quadraticCurveTo(mx - uy * w, my + ux * w, tx, ty)
    .quadraticCurveTo(mx + uy * w, my - ux * w, x, y);
}

function seed(g: Graphics, x: number, y: number, size: number, rot: number): void {
  const c = Math.cos(rot);
  const s = Math.sin(rot);
  for (let j = 0; j <= 10; j++) {
    const a = (j / 10) * TAU;
    const px = Math.cos(a) * size * (Math.cos(a) > 0 ? 1.35 : 1);
    const py = Math.sin(a) * size * 0.62;
    const x1 = x + px * c - py * s;
    const y1 = y + px * s + py * c;
    if (j === 0) g.moveTo(x1, y1);
    else g.lineTo(x1, y1);
  }
  g.closePath();
}

function bat(g: Graphics, x: number, y: number, s: number): void {
  const n = BAT.length / 2;
  for (let i = 0; i < n; i++) {
    const px = x + BAT[i * 2]! * s;
    const py = y + BAT[i * 2 + 1]! * s;
    if (i === 0) g.moveTo(px, py);
    else g.lineTo(px, py);
  }
  for (let i = n - 2; i > 0; i--) g.lineTo(x - BAT[i * 2]! * s, y + BAT[i * 2 + 1]! * s);
  g.closePath();
}

function face(g: Graphics, x: number, y: number, r: number): void {
  for (const side of [-1, 1]) {
    const ex = x + side * 0.32 * r;
    const ey = y - 0.16 * r;
    const s = 0.15 * r;
    g.moveTo(ex - s, ey + s * 0.6).lineTo(ex + s, ey + s * 0.6).lineTo(ex, ey - s * 0.85).closePath();
  }
  g.moveTo(x - 0.46 * r, y + 0.08 * r)
    .quadraticCurveTo(x, y + 0.64 * r, x + 0.46 * r, y + 0.08 * r)
    .quadraticCurveTo(x + 0.12 * r, y + 0.3 * r, x + 0.06 * r, y + 0.2 * r)
    .lineTo(x - 0.06 * r, y + 0.2 * r)
    .quadraticCurveTo(x - 0.12 * r, y + 0.3 * r, x - 0.46 * r, y + 0.08 * r)
    .closePath();
}

function candle(k: Kit, x: number, y: number, s: number, lit: boolean, wax: number, flame: number, alpha: number): void {
  const { g, pal } = k;
  const w = s * 0.62;
  const h = s * 1.25;
  if (lit) g.circle(x, y - h * 0.5, s * 1.1).fill({ color: flame, alpha: 0.16 * alpha });
  g.roundRect(x - w / 2, y - h * 0.35, w, h, w * 0.32).fill({ color: wax, alpha });
  g.stroke({ width: Math.max(1, s * 0.12), color: pal.ink, alpha: 0.55 * alpha });
  if (lit) g.ellipse(x, y - h * 0.35 - s * 0.36, s * 0.24, s * 0.38).fill({ color: lerpColor(flame, pal.rim, 0.35), alpha });
  else g.moveTo(x, y - h * 0.35).lineTo(x, y - h * 0.35 - s * 0.22).stroke({ width: Math.max(1, s * 0.08), color: pal.rim, alpha: 0.5 * alpha });
}

export const dress: WorldDress = {
  incomingCircle(k, body, color, alpha) {
    const { g, pal } = k;
    const r = body.radius;
    ribs(k, body, GOURD_RIBS, 0.08, lerpColor(color, pal.ink, 0.5), 0.42 * alpha, Math.max(1, r * 0.05));
    g.ellipse(body.x, body.y, r * 0.11 * body.scaleX, r * 0.11 * body.scaleY).fill({ color: lerpColor(pal.path, pal.ink, 0.55), alpha: 0.8 * alpha });
    k.rimLight(body, 0.5 * alpha);
  },

  mineCircle(k, body, alpha, width, fill) {
    const { g, pal } = k;
    const r = body.radius;
    const groove = lerpColor(fill, pal.ink, 0.42);
    k.trace(body);
    g.fill({ color: fill, alpha });
    k.terminator(body, lerpColor(fill, pal.heldShade, 0.8), 0.5 * alpha);
    ribs(k, body, RIBS, 0.32, groove, 0.6 * alpha, Math.max(1, r * 0.055));
    g.ellipse(body.x, body.y, r * 0.26 * body.scaleX, r * 0.26 * body.scaleY).fill({ color: lerpColor(fill, pal.heldShade, 0.7), alpha: 0.7 * alpha });
    const stem = lerpColor(pal.path, pal.ink, 0.55);
    g.moveTo(body.x + r * 0.08, body.y - r * 0.06)
      .bezierCurveTo(body.x + r * 0.3, body.y - r * 0.32, body.x + r * 0.46, body.y - r * 0.08, body.x + r * 0.34, body.y + r * 0.02);
    g.stroke({ width: Math.max(1, r * 0.035), color: stem, alpha: 0.8 * alpha, cap: 'round' });
    g.circle(body.x, body.y, r * 0.12).fill({ color: stem, alpha });
    g.stroke({ width: Math.max(1, r * 0.03), color: pal.ink, alpha: 0.7 * alpha });
    k.highlight(body, 0.45 * alpha);
    k.trace(body);
    g.stroke({ width, color: k.outline, alpha, join: 'round' });
  },

  target(k, body, alpha, _spread, reach, spin, edge) {
    const { g, pal } = k;
    k.trace(body);
    g.fill({ color: lerpColor(pal.bg, 0x000000, 0.35), alpha: 0.45 * alpha });
    k.trace(body);
    g.fill({ color: pal.spot, alpha: 0.07 * alpha });
    const steps = 72;
    for (let i = 0; i <= steps; i++) {
      const a = (i / steps) * TAU;
      const rr = reach * (1 + 0.04 * Math.sin(a * 9 + spin * 2));
      const x = body.x + Math.cos(a) * rr;
      const y = body.y + Math.sin(a) * rr;
      if (i === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.stroke({ width: Math.max(1.2, k.unit * 0.006), color: pal.path, alpha: 0.5 * alpha, join: 'round' });
    const s = Math.max(4, k.unit * 0.022);
    k.ringDots(body, reach, CANDLES, spin + Math.PI / CANDLES, (x, y, _i, a) => {
      leaf(g, x, y, s * 1.2, a + Math.PI / 2);
    });
    g.fill({ color: LEAF, alpha: 0.5 * alpha });
    const wax = lerpColor(pal.spot, pal.rim, 0.5);
    k.ringDots(body, reach, CANDLES, spin, (x, y) => {
      candle(k, x, y, s, true, wax, pal.held, 0.9 * alpha);
    });
    k.trace(body);
    g.stroke({ width: edge, color: pal.rim, alpha: 0.8 * alpha, join: 'round' });
  },

  pip(k, x, y, radius, on, last, alpha) {
    const { pal } = k;
    const tone = last ? pal.spot : pal.held;
    const wax = last ? lerpColor(pal.spot, pal.rim, 0.3) : lerpColor(pal.rim, pal.held, 0.25);
    candle(k, x, y, radius * 1.6, on, on ? wax : lerpColor(pal.bg, wax, 0.4), tone, (on ? 1 : 0.7) * alpha);
  },

  path(k, points, count, alpha) {
    const { g, pal } = k;
    const amp = k.unit * 0.012;
    const wave = k.unit * 0.11;
    const stops: number[] = [];
    let along = 0;
    let lastCrest = 0;
    for (let i = 2; i < count; i += 2) {
      const x0 = points[i - 2]!;
      const y0 = points[i - 1]!;
      const dx = points[i]! - x0;
      const dy = points[i + 1]! - y0;
      const len = Math.hypot(dx, dy);
      if (len < 1) continue;
      const nx = -dy / len;
      const ny = dx / len;
      const n = Math.max(2, Math.min(60, Math.floor(len / Math.max(3, k.unit * 0.015))));
      for (let s = 0; s <= n; s++) {
        const t = s / n;
        const d = along + len * t;
        const off = Math.sin((d / wave) * TAU) * amp;
        const x = x0 + dx * t + nx * off;
        const y = y0 + dy * t + ny * off;
        if (i === 2 && s === 0) g.moveTo(x, y);
        else g.lineTo(x, y);
        const crest = Math.floor(d / (wave * 0.5));
        if (crest !== lastCrest) stops.push(x, y, Math.atan2(dy, dx), crest % 2 === 0 ? 1 : -1);
        lastCrest = crest;
      }
      along += len;
    }
    g.stroke({ width: Math.max(1.2, k.unit * 0.005), color: pal.path, alpha: alpha * 2.4, cap: 'round', join: 'round' });
    const size = Math.max(5, k.unit * 0.026);
    for (let i = 0; i < stops.length; i += 4) {
      if ((i / 4) % 3 === 2) continue;
      leaf(g, stops[i]!, stops[i + 1]!, size, stops[i + 2]! - stops[i + 3]! * 1.1);
    }
    g.fill({ color: LEAF, alpha: alpha * 2.6 });
    for (let i = 0; i < stops.length; i += 4) {
      if ((i / 4) % 3 !== 2) continue;
      const side = stops[i + 3]!;
      const a = stops[i + 2]! - side * Math.PI / 2;
      const r = size * 0.45;
      const cx = stops[i]! + Math.cos(a) * r;
      const cy = stops[i + 1]! + Math.sin(a) * r;
      const a0 = a + Math.PI;
      g.moveTo(cx + Math.cos(a0) * r, cy + Math.sin(a0) * r).arc(cx, cy, r, a0, a0 + side * Math.PI * 1.6, side < 0);
    }
    g.stroke({ width: Math.max(1, k.unit * 0.004), color: pal.path, alpha: alpha * 2.2, cap: 'round' });
  },
};

export const fx: WorldFx = {
  bit(g, x, y, size, i, color, alpha, rot) {
    if (i % 3 === 0) {
      leaf(g, x, y, size * 2.4, rot);
      g.fill({ color: lerpColor(color, LEAF, 0.7), alpha });
      return;
    }
    seed(g, x, y, size, rot);
    g.fill({ color, alpha });
  },

  milestone(g, m, pal) {
    const r0 = m.r / (1.1 + 0.9 * m.grow);
    face(g, m.x, m.y, r0);
    g.fill({ color: pal.heldLight, alpha: 0.7 * m.fade });
    const s = m.unit * 0.026;
    for (let i = 0; i < MINIS; i++) {
      const a = (i / MINIS) * TAU - Math.PI / 2;
      const x = m.x + Math.cos(a) * m.r;
      const y = m.y + Math.sin(a) * m.r;
      const lit = smooth(m.grow * MINIS * 1.25 - i);
      g.circle(x, y, s).fill({ color: lerpColor(pal.heldShade, pal.held, lit), alpha: m.fade * (0.25 + 0.55 * lit) });
      g.ellipse(x - s * 0.36, y, s * 0.42, s * 0.9).ellipse(x + s * 0.36, y, s * 0.42, s * 0.9);
      g.stroke({ width: Math.max(1, s * 0.12), color: pal.heldShade, alpha: 0.4 * m.fade });
      g.circle(x, y - s * 0.95, s * 0.2).fill({ color: LEAF, alpha: 0.6 * m.fade });
    }
    for (let i = 0; i < BATS; i++) {
      const a = (i / BATS) * TAU + 0.5 + m.grow * 0.7;
      const d = m.r * (1.3 + 0.15 * m.grow);
      bat(g, m.x + Math.cos(a) * d, m.y + Math.sin(a) * d, m.unit * 0.034);
    }
    g.fill({ color: pal.spot, alpha: 0.42 * m.fade });
    g.stroke({ width: Math.max(1, m.width * 0.4), color: pal.rim, alpha: 0.5 * m.fade, join: 'round' });
  },

  trail(g, x, y, size) {
    g.circle(x, y, size * 0.7);
  },
};
